import React from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import {
  MONTH_ORDER,
  PERFORMANCE_RANGE_OPTIONS,
  getTargetMonths,
  type PerformanceRange,
  type TrialBalanceRow,
} from './constants';
import type { LedgerCellContext } from '../trialBalance/JournalLedgerDialog';

type Props = {
  dedupedRows: TrialBalanceRow[];
  fiscalYear: number;
  currentMonth: number;
  setCurrentMonth: (m: number) => void;
  performanceRange: PerformanceRange;
  setPerformanceRange: (range: PerformanceRange) => void;
  expandedSections: Record<string, boolean>;
  toggleSection: (sectionName: string) => void;
  expandedAccounts: Record<string, boolean>;
  toggleAccount: (accountCode: string) => void;
  /** ヘッダーに期間セレクタを表示するか。false の場合は別セクション側に設置されている想定 */
  showRangeSelector?: boolean;
  /** 実績・前年実績セルクリックで補助元帳ダイアログを開く (ページ側が状態を保持)。未指定時はクリック不可 */
  onCellClick?: (ctx: LedgerCellContext) => void;
};

// 🔽 達成率のステータスバッジ表示ロジック
const renderStatusBadge = (rate: number, diff: number, isRevenue: boolean) => {
  if (rate === 0 && diff === 0) return null;
  let text = '';
  let color = '';

  if (isRevenue) {
    if (diff >= 0) { text = 'SURPLUS'; color = '#059669'; } // 緑
    else { text = 'SHORT'; color = '#ef4444'; } // 赤
  } else {
    if (diff <= 0) { text = 'UNDER'; color = '#059669'; } // 緑
    else { text = 'OVER'; color = '#ef4444'; } // 赤
  }

  return (
    <Typography sx={{ fontSize: '10px', fontWeight: 800, color, lineHeight: 1, letterSpacing: '0.5px', mb: 0.5 }}>
      {text}
    </Typography>
  );
};

/**
 * 損益計算書テーブル（科目区分→勘定科目→補助科目のツリービュー）と月切替UI。
 * 当月の 実績 / 前年同月 / 当月予算 / 差異 / 達成率 を対比表示する。
 */
export default function ProfitAndLossDetailTable({
  dedupedRows,
  fiscalYear,
  currentMonth,
  setCurrentMonth,
  performanceRange,
  setPerformanceRange,
  expandedSections,
  toggleSection,
  expandedAccounts,
  toggleAccount,
  showRangeSelector = true,
  onCellClick,
}: Props) {
  // 該当月の暦年 (9〜12月は fiscalYear-1, 1〜8月は fiscalYear)
  const displayYear = currentMonth >= 9 ? fiscalYear - 1 : fiscalYear;

  // 単月モードか否か。単月時のみ月セレクトを有効化し、列見出しも「当月/前年同月」表記にする
  const isMonth = performanceRange === 'month';
  // 選択中の業績範囲の対象月 (単月=選択月のみ / 範囲=複数月)
  const targetMonths = getTargetMonths(performanceRange, currentMonth);
  // 業績範囲のラベル (タイトル・サブタイトル表示用)
  const rangeLabel = PERFORMANCE_RANGE_OPTIONS.find(o => o.value === performanceRange)?.label ?? '単月';

  // 補助元帳ドリルダウン: 実績・前年実績セルに渡す文脈を組み立てる (単月=month / 範囲=months で期間指定)
  const makeLedgerCtx = (
    base: Pick<LedgerCellContext, 'accountCode' | 'accountName' | 'subAccountCode' | 'subAccountName'>,
    amount: number,
    prevYear: boolean,
  ): LedgerCellContext => ({
    ...base,
    month: isMonth ? currentMonth : null,
    months: isMonth ? undefined : targetMonths,
    periodLabel: isMonth ? undefined : rangeLabel,
    prevYear: prevYear || undefined,
    amount,
  });
  // セルを「ドリルダウン可能」に見せる共通スタイル (onCellClick 未指定時は付与しない)
  const clickableAmountSx = onCellClick
    ? ({ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e7ff', textDecoration: 'underline' } } as const)
    : ({} as const);
  // 列見出し (単月は当月基準、範囲は期間合計基準)
  const colActual = isMonth ? '当月実績' : '実績';
  const colPrev = isMonth ? '前年同月' : '前年実績';
  const colBudget = isMonth ? '当月予算' : '予算';

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* 業績サマリーカードの補足の位置付けのため、アイコンなし・控えめなタイトルにする */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
            {rangeLabel} 損益計算書
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
            {isMonth
              ? `${displayYear}年${currentMonth}月度 実績 vs 予算`
              : `${fiscalYear}年度 ${rangeLabel} 実績 vs 予算`}
          </Typography>
        </Box>

        {/* 期間セレクタ — 単月(月セレクト) / 四半期 / 半期 / 通期を1本のラジオに統合。
            この選択は業績サマリー・部署別ダッシュボードと共有され、同時に切り替わる */}
        {showRangeSelector && (
        <RadioGroup
          row
          value={performanceRange}
          onChange={(e) => setPerformanceRange(e.target.value as PerformanceRange)}
          sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}
        >
          {PERFORMANCE_RANGE_OPTIONS.map(opt => (
            <React.Fragment key={opt.value}>
              <FormControlLabel
                value={opt.value}
                control={<Radio size="small" sx={{ py: 0.25 }} />}
                label={opt.label}
                sx={{ mr: 0.25, '& .MuiFormControlLabel-label': { fontSize: '13px', fontWeight: 600, color: '#475569' } }}
              />
              {/* 単月ラジオの直後に月セレクトを配置。単月選択時のみ有効 */}
              {opt.value === 'month' && (
                <FormControl size="small" sx={{ minWidth: 84, mr: 2 }}>
                  <Select
                    value={currentMonth}
                    onChange={(e) => setCurrentMonth(Number(e.target.value))}
                    disabled={!isMonth}
                    sx={{ height: '30px', backgroundColor: '#fff' }}
                  >
                    {MONTH_ORDER.map(m => (
                      <MenuItem key={m} value={m}>
                        {m}月
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </React.Fragment>
          ))}
        </RadioGroup>
        )}
      </Box>
      <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.12)', overflow: 'auto', maxHeight: '65vh' }}>
        <Table stickyHeader size="small" sx={{ width: '100%', minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', width: '100%', minWidth: '220px', position: 'sticky', left: 0, zIndex: 11 }}>
                科目区分 / 勘定科目
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '110px', zIndex: 1 }}>
                {colActual}
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '110px', zIndex: 1 }}>
                {colPrev}
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '110px', zIndex: 1 }}>
                {colBudget}
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '110px', zIndex: 1 }}>
                差異
              </TableCell>
              <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', color: 'grey.500', fontWeight: 500, fontSize: '0.8125rem', whiteSpace: 'nowrap', minWidth: '90px', zIndex: 1 }}>
                達成率
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              // グループ化 (dedupedRows ベース 「補」と同 account の「科」は除外済み)
              const groups: Record<string, TrialBalanceRow[]> = {};
              dedupedRows.forEach(row => {
                const grp = row.category_l2_name || 'その他';
                if (!groups[grp]) groups[grp] = [];
                groups[grp].push(row);
              });

              const sectionTotals: Record<string, { actual: number; budget: number; actualPrev: number }> = {};

              const renderDetailSection = (sectionName: string, isRevenue: boolean) => {
                const groupRows = groups[sectionName];
                if (!groupRows) return null;

                // 科目・補助科目ごとに集約
                const accountAgg: Record<string, {
                  name: string;
                  actual: number;
                  budget: number;
                  actualPrev: number;
                  category: string;
                  subAccounts: Record<string, { name: string; actual: number; budget: number; actualPrev: number }>;
                }> = {};

                groupRows.forEach(row => {
                  const accCode = row.account_code || 'unknown';
                  const subAccCode = row.sub_account_code || '';
                  // 選択中の業績範囲の対象月を合算 (単月は1ヶ月、四半期/半期/通期は複数月)
                  const actual = targetMonths.reduce((s, m) => s + (Number(row[`month_${m}_actual`]) || 0), 0);
                  const budget = targetMonths.reduce((s, m) => s + (Number(row[`month_${m}_budget`]) || 0), 0);
                  const actualPrev = targetMonths.reduce((s, m) => s + (Number(row[`month_${m}_actual_prev`]) || 0), 0);

                  if (!accountAgg[accCode]) {
                    accountAgg[accCode] = {
                      name: row.account_name || '-',
                      actual: 0,
                      budget: 0,
                      actualPrev: 0,
                      category: row.category_l2_name || '',
                      subAccounts: {},
                    };
                  }
                  accountAgg[accCode].actual += actual;
                  accountAgg[accCode].budget += budget;
                  accountAgg[accCode].actualPrev += actualPrev;

                  if (subAccCode) {
                    if (!accountAgg[accCode].subAccounts[subAccCode]) {
                      accountAgg[accCode].subAccounts[subAccCode] = {
                        name: row.sub_account_name || '-',
                        actual: 0,
                        budget: 0,
                        actualPrev: 0,
                      };
                    }
                    accountAgg[accCode].subAccounts[subAccCode].actual += actual;
                    accountAgg[accCode].subAccounts[subAccCode].budget += budget;
                    accountAgg[accCode].subAccounts[subAccCode].actualPrev += actualPrev;
                  }
                });

                const entries = Object.entries(accountAgg);
                let sectionActual = 0;
                let sectionBudget = 0;
                let sectionActualPrev = 0;
                entries.forEach(([, v]) => {
                  sectionActual += v.actual;
                  sectionBudget += v.budget;
                  sectionActualPrev += v.actualPrev;
                });

                sectionTotals[sectionName] = { actual: sectionActual, budget: sectionBudget, actualPrev: sectionActualPrev };

                const isSectionExpanded = !!expandedSections[sectionName];
                const sectionDiff = sectionActual - sectionBudget;
                const sectionRate = sectionBudget !== 0 ? (sectionActual / sectionBudget * 100) : (sectionActual > 0 ? 0 : 0);

                return (
                  <React.Fragment key={sectionName}>
                    <TableRow
                      onClick={() => toggleSection(sectionName)}
                      sx={{ backgroundColor: '#ffffff', cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                    >
                      <TableCell sx={{ fontWeight: 800, color: '#334155', position: 'sticky', left: 0, zIndex: 9, borderBottom: '1px solid #f1f5f9' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton size="small" sx={{ mr: 1, p: 0.5, color: '#94a3b8' }}>
                            {isSectionExpanded ? <Icons.ExpandMore fontSize="small" /> : <Icons.KeyboardArrowRight fontSize="small" />}
                          </IconButton>
                          {sectionName.replace(/計$/, '')}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, borderBottom: '1px solid #f1f5f9' }}>
                        {sectionActual.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>
                        {sectionActualPrev !== 0 ? sectionActualPrev.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: '#60a5fa', borderBottom: '1px solid #f1f5f9' }}>
                        {sectionBudget !== 0 ? sectionBudget.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: sectionDiff >= 0 ? '#059669' : '#ef4444', borderBottom: '1px solid #f1f5f9' }}>
                        {sectionBudget !== 0 ? (sectionDiff >= 0 ? '+' : '') + sectionDiff.toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, borderBottom: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                        {sectionBudget !== 0 && renderStatusBadge(sectionRate, sectionDiff, isRevenue)}
                        {sectionBudget !== 0 ? `${sectionRate.toFixed(1)}%` : '-'}
                      </TableCell>
                    </TableRow>

                    {isSectionExpanded && entries.map(([code, data]) => {
                      const diff = data.actual - data.budget;
                      const rate = data.budget !== 0 ? (data.actual / data.budget * 100) : (data.actual > 0 ? 0 : 0);
                      const hasSubAccounts = Object.keys(data.subAccounts).length > 0;
                      const isAccExpanded = !!expandedAccounts[code];

                      return (
                        <React.Fragment key={code}>
                          <TableRow
                            hover
                            onClick={() => hasSubAccounts && toggleAccount(code)}
                            sx={{ '&:hover': { backgroundColor: '#f5f5f5' }, backgroundColor: '#ffffff', cursor: hasSubAccounts ? 'pointer' : 'default' }}
                          >
                            <TableCell sx={{ fontWeight: 500, color: '#475569', position: 'sticky', left: 0, zIndex: 9, pl: 4, borderBottom: '1px dashed #f1f5f9' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {hasSubAccounts ? (
                                  <IconButton size="small" sx={{ mr: 1, p: 0.5, color: '#94a3b8' }}>
                                    {isAccExpanded ? <Icons.ExpandMore fontSize="small" /> : <Icons.KeyboardArrowRight fontSize="small" />}
                                  </IconButton>
                                ) : (
                                  <Box sx={{ width: 28, height: 28, mr: 1 }} />
                                )}
                                {data.name}
                              </Box>
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={(e) => { e.stopPropagation(); onCellClick?.(makeLedgerCtx({ accountCode: code, accountName: data.name }, data.actual, false)); }}
                              sx={{ fontSize: '14px', fontWeight: 600, borderBottom: '1px dashed #f1f5f9', ...clickableAmountSx }}
                            >
                              {data.actual !== 0 ? data.actual.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={(e) => { e.stopPropagation(); onCellClick?.(makeLedgerCtx({ accountCode: code, accountName: data.name }, data.actualPrev, true)); }}
                              sx={{ fontSize: '14px', color: '#94a3b8', borderBottom: '1px dashed #f1f5f9', ...clickableAmountSx }}
                            >
                              {data.actualPrev !== 0 ? data.actualPrev.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '14px', color: '#60a5fa', borderBottom: '1px dashed #f1f5f9' }}>
                              {data.budget !== 0 ? data.budget.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 600, color: diff >= 0 ? '#059669' : '#ef4444', borderBottom: '1px dashed #f1f5f9' }}>
                              {data.budget !== 0 ? (diff >= 0 ? '+' : '') + diff.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 700, borderBottom: '1px dashed #f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                              {data.budget !== 0 && renderStatusBadge(rate, diff, isRevenue)}
                              {data.budget !== 0 ? `${rate.toFixed(1)}%` : '-'}
                            </TableCell>
                          </TableRow>

                          {hasSubAccounts && isAccExpanded && Object.entries(data.subAccounts).map(([subCode, subData]) => {
                            const subDiff = subData.actual - subData.budget;
                            const subRate = subData.budget !== 0 ? (subData.actual / subData.budget * 100) : (subData.actual > 0 ? 0 : 0);

                            return (
                              <TableRow key={`${code}-${subCode}`} hover sx={{ '&:hover': { backgroundColor: '#f5f5f5' }, backgroundColor: '#ffffff' }}>
                                <TableCell sx={{ fontSize: '13px', color: '#64748b', pl: 10, position: 'sticky', left: 0, zIndex: 9, borderBottom: '1px dashed #f1f5f9' }}>
                                  {subData.name}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  onClick={(e) => { e.stopPropagation(); onCellClick?.(makeLedgerCtx({ accountCode: code, accountName: data.name, subAccountCode: subCode, subAccountName: subData.name }, subData.actual, false)); }}
                                  sx={{ fontSize: '13px', fontWeight: 500, color: '#475569', borderBottom: '1px dashed #f1f5f9', ...clickableAmountSx }}
                                >
                                  {subData.actual !== 0 ? subData.actual.toLocaleString() : '-'}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  onClick={(e) => { e.stopPropagation(); onCellClick?.(makeLedgerCtx({ accountCode: code, accountName: data.name, subAccountCode: subCode, subAccountName: subData.name }, subData.actualPrev, true)); }}
                                  sx={{ fontSize: '13px', color: '#94a3b8', borderBottom: '1px dashed #f1f5f9', ...clickableAmountSx }}
                                >
                                  {subData.actualPrev !== 0 ? subData.actualPrev.toLocaleString() : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '13px', color: '#60a5fa', borderBottom: '1px dashed #f1f5f9' }}>
                                  {subData.budget !== 0 ? subData.budget.toLocaleString() : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500, color: subDiff >= 0 ? '#059669' : '#ef4444', borderBottom: '1px dashed #f1f5f9' }}>
                                  {subData.budget !== 0 ? (subDiff >= 0 ? '+' : '') + subDiff.toLocaleString() : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 600, borderBottom: '1px dashed #f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                                  {subData.budget !== 0 && renderStatusBadge(subRate, subDiff, isRevenue)}
                                  {subData.budget !== 0 ? `${subRate.toFixed(1)}%` : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              };

              const renderProfitDetailRow = (title: string, actual: number, budget: number, actualPrev: number = 0) => {
                const diff = actual - budget;
                const rate = budget !== 0 ? (actual / budget * 100) : (actual > 0 ? 0 : 0);
                return (
                  <TableRow key={title} sx={{ backgroundColor: '#eff6ff' }}>
                    <TableCell sx={{ fontWeight: 900, color: '#1e3a8a', position: 'sticky', left: 0, zIndex: 9, borderBottom: '3px solid #bfdbfe' }}>
                      {title}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: '#1e3a8a', borderBottom: '3px solid #bfdbfe' }}>
                      {actual.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: '#64748b', borderBottom: '3px solid #bfdbfe' }}>
                      {actualPrev !== 0 ? actualPrev.toLocaleString() : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: '#4338ca', borderBottom: '3px solid #bfdbfe' }}>
                      {budget !== 0 ? budget.toLocaleString() : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: diff >= 0 ? '#059669' : '#ef4444', borderBottom: '3px solid #bfdbfe' }}>
                      {budget !== 0 ? (diff >= 0 ? '+' : '') + diff.toLocaleString() : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: rate >= 100 ? '#059669' : '#f59e0b', backgroundColor: '#dbeafe', borderBottom: '3px solid #bfdbfe', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
                      {budget !== 0 && renderStatusBadge(rate, diff, true)}
                      {budget !== 0 ? `${rate.toFixed(1)}%` : '-'}
                    </TableCell>
                  </TableRow>
                );
              };

              return (
                <React.Fragment>
                  {/* ① 純売上高 と ② 完成工事原価 の描画 */}
                  {renderDetailSection('純売上高', true)}
                  {renderDetailSection('完成工事原価', false)}

                  {/* 🟩 ③ 売上総利益 の計算と描画 */}
                  {(() => {
                    const salesA = sectionTotals['純売上高']?.actual || 0;
                    const salesB = sectionTotals['純売上高']?.budget || 0;
                    const salesP = sectionTotals['純売上高']?.actualPrev || 0;
                    const cogsA = sectionTotals['完成工事原価']?.actual || 0;
                    const cogsB = sectionTotals['完成工事原価']?.budget || 0;
                    const cogsP = sectionTotals['完成工事原価']?.actualPrev || 0;
                    const grossA = salesA - cogsA;
                    const grossB = salesB - cogsB;
                    const grossP = salesP - cogsP;

                    return (
                      <React.Fragment>
                        {renderProfitDetailRow('売上総利益', grossA, grossB, grossP)}

                        {/* ④ 販売費及び一般管理費計 の描画 */}
                        {renderDetailSection('販売費及び一般管理費計', false)}

                        {/* 🟩 ⑤ 営業利益 の計算と描画 */}
                        {(() => {
                          const sgaA = sectionTotals['販売費及び一般管理費計']?.actual || 0;
                          const sgaB = sectionTotals['販売費及び一般管理費計']?.budget || 0;
                          const sgaP = sectionTotals['販売費及び一般管理費計']?.actualPrev || 0;
                          const opA = grossA - sgaA;
                          const opB = grossB - sgaB;
                          const opP = grossP - sgaP;

                          return (
                            <React.Fragment>
                              {renderProfitDetailRow('営業利益', opA, opB, opP)}
                              {/* 営業外収益・営業外費用・経常利益は表示対象外 */}
                            </React.Fragment>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })()}
                </React.Fragment>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
